// menschenGruppen.ts — Aufteilung der Firmenkarte „Verantwortung fürs
// Unternehmen" in getrennte Listen (User 28.09.2026):
//
//   Unternehmensleitung · Gesellschafter · Verantwortung fürs Unternehmen
//   (alles andere) · Mensch im Unternehmen bzw. im Verein
//
// Gespeichert bleibt EIN Eintrag je Person (`rel_menschen`, attach-menschen
// ersetzt die Rollen eines Paars als Ganzes). Die Listen sind nur Sichten
// darauf: Ein geschäftsführender Gesellschafter steht in zwei Listen, und
// „Entfernen" in einer Liste nimmt nur die Rollen DIESER Liste weg.
//
// Bewusst ohne React, damit die Regel per Vitest prüfbar ist.

import type { RollenKategorie, Verantwortlichkeit } from "../../services/verantwortlichkeitService";
import type { VerantRole, VerantwortlichkeitValue } from "../verantwortlichkeit/VerantwortlichkeitInput";
import { ohneAnteil, rolleText, rollenAnteil } from "./beziehungsAnzeige";

export type MenschGruppe = "leitung" | "gesellschafter" | "verantwortung" | "mensch";
export type RollenGruppe = Exclude<MenschGruppe, "mensch">;

/** Bezeichnung einer Rolle → ihre Kategorie im Rollen-Katalog (oder unbekannt). */
export type KategorieVon = (bezeichnung: string) => RollenKategorie | undefined;

const norm = (s?: string | null) => (s ?? "").trim().toLowerCase();

/**
 * Nachschlagen „Bezeichnung → Kategorie". Der Katalog führt manche
 * Bezeichnungen in zwei Kategorien („Inhaber", „Komplementär": LEITUNG und
 * GESELLSCHAFTER) — dann gewinnt die Leitung, wie im Backend (`organKategorie`).
 */
export function baueKategorieVon(katalog: Verantwortlichkeit[]): KategorieVon {
  const rang: Record<RollenKategorie, number> = {
    LEITUNG: 4,
    GESELLSCHAFTER: 3,
    VERTRETUNG: 2,
    AUFSICHT: 1,
    WEITERE: 0,
  };
  const m = new Map<string, RollenKategorie>();
  for (const v of katalog) {
    const key = norm(v.bezeichnung);
    if (!key) continue;
    const k = v.kategorie ?? "WEITERE";
    const alt = m.get(key);
    if (!alt || rang[k] > rang[alt]) m.set(key, k);
  }
  return (bezeichnung) => m.get(norm(bezeichnung));
}

/** In welche Liste gehört EINE Rolle? */
export function rollenGruppe(r: VerantRole, kategorieVon: KategorieVon): RollenGruppe {
  if (r.kind === "gf") return "leitung";
  if (r.kind === "gesellschafter") return "gesellschafter";
  const k = kategorieVon(r.verantwortlichkeit);
  if (k === "LEITUNG") return "leitung";
  if (k === "GESELLSCHAFTER") return "gesellschafter";
  return "verantwortung";
}

type MitVerant = { verant: VerantwortlichkeitValue | null };

/** Alle Listen, in denen ein Eintrag erscheint. */
export function gruppenVon(entry: MitVerant, kategorieVon: KategorieVon): Set<MenschGruppe> {
  const out = new Set<MenschGruppe>();
  for (const r of entry.verant?.roles ?? []) out.add(rollenGruppe(r, kategorieVon));
  if (entry.verant?.istMenschImUnternehmen) out.add("mensch");
  return out;
}

/** Die Rollen eines Eintrags, die in einer Liste stehen. */
export function rollenDerGruppe(
  entry: MitVerant,
  gruppe: MenschGruppe,
  kategorieVon: KategorieVon,
): VerantRole[] {
  const roles = entry.verant?.roles ?? [];
  if (gruppe === "mensch") return roles;
  return roles.filter((r) => rollenGruppe(r, kategorieVon) === gruppe);
}

/** Die Rollen eines Eintrags, die in einer der Listen stehen (Reihenfolge wie erfasst). */
export function rollenDerGruppen(
  entry: MitVerant,
  gruppen: MenschGruppe[],
  kategorieVon: KategorieVon,
): VerantRole[] {
  if (gruppen.includes("mensch")) return entry.verant?.roles ?? [];
  return (entry.verant?.roles ?? []).filter((r) => gruppen.includes(rollenGruppe(r, kategorieVon)));
}

/**
 * Zeilentext „Rolle" eines Eintrags in einer (oder mehreren zusammengelegten)
 * Liste(n). `ohneAnteil`: Die Quote steht in einer eigenen Spalte „Anteile".
 */
export function gruppenRolleText(
  entry: MitVerant,
  gruppe: MenschGruppe | MenschGruppe[],
  kategorieVon: KategorieVon,
  menschLabel = "Mensch im Unternehmen",
  opts: { ohneAnteil?: boolean } = {},
): string {
  const gruppen = Array.isArray(gruppe) ? gruppe : [gruppe];
  const text = (r: VerantRole) => rolleText(opts.ohneAnteil ? ohneAnteil(r) : r);
  const rollen = rollenDerGruppen(entry, gruppen, kategorieVon).map(text).filter(Boolean);
  if (gruppen.length === 1 && gruppen[0] === "mensch") return rollen.length ? rollen.join(", ") : menschLabel;
  const extern = gruppen.includes("verantwortung") && entry.verant?.isExtern ? "extern · " : "";
  return extern + rollen.join(", ");
}

/** Spalte „Anteile": die Quoten der Rollen in diesen Listen („50 %", mehrere mit Komma). */
export function gruppenAnteile(
  entry: MitVerant,
  gruppe: MenschGruppe | MenschGruppe[],
  kategorieVon: KategorieVon,
): string {
  const gruppen = Array.isArray(gruppe) ? gruppe : [gruppe];
  return rollenDerGruppen(entry, gruppen, kategorieVon)
    .map(rollenAnteil)
    .filter(Boolean)
    .join(", ");
}

/**
 * „Entfernen" in EINER Liste: nur deren Rollen fallen weg (bzw. bei „Mensch im
 * Unternehmen" der Haken). Bleibt danach nichts übrig, fällt der ganze Eintrag
 * weg (`null`).
 *
 * Ein nur ABGELEITETER Haken hält den Eintrag nicht: Wer den Geschäftsführer aus
 * der Leitung entfernt, meint die Person — dass sie als Geschäftsführer
 * automatisch „im Unternehmen" zählte, ist dafür kein Grund, sie zu behalten.
 * Nur ein von Hand gesetzter Haken bleibt stehen.
 */
export function ohneGruppe<T extends MitVerant>(
  entry: T,
  gruppe: MenschGruppe,
  kategorieVon: KategorieVon,
): T | null {
  const v = entry.verant;
  if (!v) return null;
  if (gruppe === "mensch") {
    if (!v.roles.length) return null;
    return {
      ...entry,
      verant: { ...v, istMenschImUnternehmen: false, istMenschImUnternehmenManuell: true },
    };
  }
  let roles = v.roles.filter((r) => rollenGruppe(r, kategorieVon) !== gruppe);
  // Ohne Leitung ist der Gesellschafter nicht mehr geschäftsführend.
  if (!roles.some((r) => rollenGruppe(r, kategorieVon) === "leitung")) {
    roles = roles.map((r) => (r.kind === "gesellschafter" ? { ...r, istGeschaeftsfuehrer: false } : r));
  }
  const haken = !!v.istMenschImUnternehmen && !!v.istMenschImUnternehmenManuell;
  if (!roles.length && !haken) return null;
  return {
    ...entry,
    verant: {
      ...v,
      roles,
      // Der abgeleitete Haken folgt den Rollen: ohne Rollen fällt er weg.
      istMenschImUnternehmen: roles.length ? v.istMenschImUnternehmen : haken,
    },
  };
}

/** „Entfernen" in einer zusammengelegten Liste: nacheinander je Teil-Liste. */
export function ohneGruppen<T extends MitVerant>(
  entry: T,
  gruppen: MenschGruppe[],
  kategorieVon: KategorieVon,
): T | null {
  let rest: T | null = entry;
  for (const g of gruppen) {
    if (!rest) return null;
    rest = ohneGruppe(rest, g, kategorieVon);
  }
  return rest;
}
